// App-defined storage ceiling for IndexedDB/Dexie.
// IndexedDB performance degrades noticeably above ~2 GB on most devices:
//   < 500 MB  — ideal (fast scans, instant startup)
//   500 MB–2 GB — good (acceptable with proper indexes)
//   2 GB–5 GB — degraded (table scans slow, startup lag)
//   > 5 GB    — poor (queries timeout on low-RAM devices)
// We cap at 2 GB regardless of what the OS would permit (which can be 60%+ of disk).
export const APP_STORAGE_CAP_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB

export const STORAGE_PERSISTENCE_THRESHOLDS = {
  warning: 70,
  critical: 85,
} as const;

// Flat convenience re-exports of the threshold values. These exist so that
// callers can `import { STORAGE_WARNING_THRESHOLD, STORAGE_CRITICAL_THRESHOLD }`
// without dereferencing the constants record. Always derived from
// STORAGE_PERSISTENCE_THRESHOLDS — never set independently.
export const STORAGE_WARNING_THRESHOLD = STORAGE_PERSISTENCE_THRESHOLDS.warning;
export const STORAGE_CRITICAL_THRESHOLD = STORAGE_PERSISTENCE_THRESHOLDS.critical;

// ---------------------------------------------------------------------------
// Platform detection — used to tailor storage warning messages to the actual
// device and browser rather than showing generic or wrong-platform text.
// ---------------------------------------------------------------------------

export type DetectedPlatform =
  | 'windows-chrome'
  | 'windows-edge'
  | 'windows-firefox'
  | 'windows-other'
  | 'macos-safari'
  | 'macos-chrome'
  | 'macos-other'
  | 'android-chrome'
  | 'android-other'
  | 'ios-safari'
  | 'ios-other'
  | 'unknown';

export function detectPlatform(): DetectedPlatform {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;

  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(ua.includes('Windows') || ua.includes('Android'));
  const isAndroid = /Android/.test(ua);
  const isMac = /Macintosh|MacIntel/.test(ua) && !isIOS;
  const isWindows = /Windows/.test(ua);

  const isChrome = /Chrome\//.test(ua) && !/Edg\//.test(ua);
  const isEdge = /Edg\//.test(ua);
  const isFirefox = /Firefox\//.test(ua);
  const isSafari = /Safari\//.test(ua) && !isChrome && !isEdge;

  if (isIOS) return isSafari ? 'ios-safari' : 'ios-other';
  if (isAndroid) return isChrome ? 'android-chrome' : 'android-other';
  if (isWindows) {
    if (isEdge) return 'windows-edge';
    if (isChrome) return 'windows-chrome';
    if (isFirefox) return 'windows-firefox';
    return 'windows-other';
  }
  if (isMac) {
    if (isSafari) return 'macos-safari';
    if (isChrome) return 'macos-chrome';
    return 'macos-other';
  }
  return 'unknown';
}

export interface StorageMessage {
  title: string;
  description: string;
  warning: string;
}

/** Returns a platform-specific storage persistence warning message. */
export function getStoragePersistenceMessage(
  outcome: 'denied' | 'unsupported' | 'error',
  platform?: DetectedPlatform,
): StorageMessage {
  const p = platform ?? detectPlatform();

  const descriptions: Partial<Record<DetectedPlatform, string>> = {
    'windows-chrome':
      'Chrome on Windows did not grant persistent storage. '
      + 'This usually happens when Chrome is in guest mode or when site permissions have been restricted.',
    'windows-edge':
      'Microsoft Edge on Windows did not grant persistent storage. '
      + 'Edge ties persistence to whether the site is "installed" as a PWA or listed as an important site.',
    'windows-firefox':
      'Firefox on Windows did not grant persistent storage. '
      + 'Firefox requires either user-initiated permission or that the site is bookmarked before it grants persistence.',
    'windows-other':
      'Your browser on Windows did not grant persistent storage. '
      + 'This is uncommon on desktop and typically means the browser is in a restricted or private mode.',
    'macos-safari':
      'Safari on macOS did not grant persistent storage. '
      + 'Safari enforces a 7-day inactivity eviction policy unless the app is added to the Dock via File > Add to Dock.',
    'macos-chrome':
      'Chrome on macOS did not grant persistent storage. '
      + 'This usually resolves by installing the app — open the address bar menu and choose "Install Defensible".',
    'macos-other':
      'Your browser on macOS did not grant persistent storage. '
      + 'macOS browsers may evict unprotected storage under disk pressure.',
    'android-chrome':
      'Chrome on Android did not grant persistent storage. '
      + 'Chrome grants persistence automatically when the app is installed to the home screen via "Add to Home screen".',
    'android-other':
      'Your browser on Android did not grant persistent storage. '
      + 'For reliable offline access on Android, install the app to your home screen.',
    'ios-safari':
      'Safari on iOS did not grant persistent storage. '
      + 'iOS requires the app to be added to the Home Screen before it can protect local data from eviction.',
    'ios-other':
      'Your browser on iOS did not grant persistent storage. '
      + 'iOS only grants persistent storage to apps installed via Safari\'s "Add to Home Screen" feature.',
  };

  const warnings: Partial<Record<DetectedPlatform, string>> = {
    'windows-chrome':
      'To fix: open Chrome menu → Settings → Privacy and security → Site settings → find this site → '
      + 'set Storage to "Allow". Alternatively, install the app via the address bar install icon.',
    'windows-edge':
      'To fix: install the app via the Edge address bar (look for the install icon on the right side). '
      + 'Once installed, Edge will protect storage automatically.',
    'windows-firefox':
      'To fix: bookmark this site (Ctrl+D), then reload. Firefox grants persistence to bookmarked sites. '
      + 'You can also go to about:preferences#privacy and grant storage permissions manually.',
    'windows-other':
      'Check that you are not using private/incognito mode, and that this site has not had storage blocked '
      + 'in your browser\'s site permissions. Installing the app is the most reliable solution.',
    'macos-safari':
      'To fix on macOS Safari: go to File → Add to Dock (or use the Share button → Add to Dock). '
      + 'This unlocks persistent storage and removes the 7-day eviction limit.',
    'macos-chrome':
      'To fix: click the install icon in the Chrome address bar (right side) to install the app. '
      + 'Once installed, Chrome will protect storage from eviction.',
    'macos-other':
      'Install the app if your browser supports it, or use Chrome/Edge for the most reliable persistent storage on macOS.',
    'android-chrome':
      'To fix: tap the Chrome menu (⋮) → "Add to Home screen". '
      + 'Once installed, Chrome will grant full persistent storage automatically.',
    'android-other':
      'Open this app in Chrome and use "Add to Home screen" for persistent storage. '
      + 'Unprotected data may be evicted when your device runs low on storage.',
    'ios-safari':
      'To fix on iOS: tap the Share button (□↑) → "Add to Home Screen" → "Add". '
      + 'Reopen the app from the Home Screen icon — Safari will then grant persistent storage.',
    'ios-other':
      'iOS only supports persistent storage for apps opened from the Home Screen via Safari. '
      + 'Open this app in Safari, then use Share → "Add to Home Screen".',
    'unknown':
      'Installing the app to your home screen or desktop is the most reliable way to ensure '
      + 'local data is not evicted under storage pressure.',
  };

  const defaultDescription =
    'Persistent browser storage was not granted. Local-first data may be evicted by the browser under storage pressure.';
  const defaultWarning =
    'Install this app to your home screen or desktop, then reopen it. '
    + 'This is the most reliable way to protect offline data across all platforms.';

  return {
    title: 'Storage Protection Required',
    description: descriptions[p] ?? defaultDescription,
    warning: warnings[p] ?? defaultWarning,
  };
}

/** Returns a platform-specific critical storage pressure message. */
export function getStorageCriticalMessage(percentUsed: number): StorageMessage {
  const p = detectPlatform();

  const isMobile = p.startsWith('ios') || p.startsWith('android');
  const isDesktop = p.startsWith('windows') || p.startsWith('macos');

  const deviceHint = isMobile
    ? 'Free space on your device by deleting unused apps or media, then retry.'
    : isDesktop
      ? 'Free disk space on this machine by emptying the recycle bin or removing large files, then reload.'
      : 'Free up device storage and reload the app.';

  return {
    title: 'Critical Storage Pressure',
    description:
      `Local storage is at ${percentUsed.toFixed(1)}% of the 2 GB recommended limit. `
      + 'Offline data is at risk of being truncated or evicted.',
    warning:
      `Stop heavy operations immediately. Ensure the server is online and all data has synced. `
      + deviceHint,
  };
}

export const STORAGE_PERSISTENCE_EVENTS = {
  persistUnsupported: 'storage_persist_unsupported',
  persistAlreadyGranted: 'storage_persist_already_granted',
  persistGranted: 'storage_persist_granted',
  persistDenied: 'storage_persist_denied',
  persistError: 'storage_persist_error',
  estimateUnsupported: 'storage_estimate_unsupported',
  estimateWarning: 'storage_estimate_warning',
  estimateCritical: 'storage_estimate_critical',
  estimateError: 'storage_estimate_error',
} as const;

export type StoragePersistenceEventName =
  (typeof STORAGE_PERSISTENCE_EVENTS)[keyof typeof STORAGE_PERSISTENCE_EVENTS];
